import { ActionType, Listing, ListingColumn } from "o4a-react";
import * as React from "react";
import * as Messages from "../../codegen/Messages";
import { DetailsPanelId, PageId } from "../../constants/pluginConstants";
import { ListingTestIds, MonochromeIconIds } from "../../constants/uiConstants";
import { CloudVmCluster } from "../../gen/clients/mchub-azure-api-client-exa";
import { useAnalytics } from "../../hooks/useAnalytics";
import { useOperation } from "../../hooks/useOperation";
import { useSidePanel } from "../../hooks/useSidePanel";
import {
  ExaVmClusterAddSshKeyPairProps,
  newExaVmClusterAddSshKeyPair,
} from "../../operations/ExaVmCluster/ExaVmClusterAddSshKeyPair";

export enum ActionIds {
  AddSshKey = "add-ssh-key",
  Refresh = "refresh",
  CopySshPublicKey = "copy-ssh-public-key",
}

export enum ActionBarTestIds {
  AddSshKey = "add-ssh-key",
  Refresh = "refresh",
}

export enum ListingActionTestIds {
  CopySshPublicKey = "copy-ssh-public-key",
}

export interface ExaVmClusterSecurityDetailsProps {
  vmCluster: CloudVmCluster | undefined;
  location: string;
  refresh: () => void;
}

export const ExaVmClusterSecurityDetails = (
  { vmCluster, location, refresh }: ExaVmClusterSecurityDetailsProps,
): JSX.Element => {
  const { trackActionClick, trackActionDiscard } = useAnalytics();

  const disableAddSshKeyAction = !vmCluster;
  const { trigger: triggerAddSshKey } = useOperation<ExaVmClusterAddSshKeyPairProps>(newExaVmClusterAddSshKeyPair);
  const { closePanels } = useSidePanel();

  const securityActionItems: ActionType[] = [
    {
      key: ActionIds.AddSshKey,
      testId: ActionBarTestIds.AddSshKey,
      text: Messages.actions.addSshKey(),
      icon: MonochromeIconIds.Create,
      onClick: () => {
        trackActionClick(ActionIds.AddSshKey, PageId.VMCLUSTER_DETAILS, DetailsPanelId.SECURITY);
        triggerAddSshKey({
          vmClusterId: vmCluster?.id,
          sshPublicKeys: vmCluster?.sshPublicKeys,
          location,
          onExecute: refresh,
          onCancel: () => trackActionDiscard(ActionIds.AddSshKey, PageId.VMCLUSTER_DETAILS, DetailsPanelId.SECURITY),
        });
      },
      disabled: disableAddSshKeyAction,
    },
    {
      key: ActionIds.Refresh,
      testId: ActionBarTestIds.Refresh,
      text: Messages.actions.refresh(),
      icon: MonochromeIconIds.Refresh,
      onClick: () => {
        trackActionClick(ActionIds.Refresh, PageId.VMCLUSTER_DETAILS, DetailsPanelId.SECURITY);
        refresh();
      },
    },
  ];

  const publicSshKeys: { publicKey: string }[] = vmCluster?.sshPublicKeys?.map(entry => ({ publicKey: entry })) || [];

  const columns: ListingColumn[] = [
    {
      itemProp: "publicKey",
      name: Messages.labels.sshPublicKey(),
      flexGrow: 1,
    },
  ];

  return (

    <Listing
      testId={ListingTestIds.ExaVmClusterSshKeys}
      actionBarItems={securityActionItems}
      onActionClick={closePanels}
      items={publicSshKeys}
      emptyList={{ title: Messages.common.noResults() }}
      listColumns={columns}
      actions={[
        {
          key: ActionIds.CopySshPublicKey,
          testId: ListingActionTestIds.CopySshPublicKey,
          text: Messages.actions.copySshPublicKey(),
          icon: MonochromeIconIds.Copy,
          onClick: value => {
            trackActionClick(ActionIds.CopySshPublicKey, PageId.VMCLUSTER_DETAILS, DetailsPanelId.SECURITY);
            navigator.clipboard.writeText(value?.publicKey || "");
          },
        },
      ]}
    />

  );
};
